import { useCallback } from "react";
import { useAppDispatch } from "../../redux/store";
import { updatePlayerDetails } from "../../redux/slices/menu";
import { useSmartNavigate } from "../useSmartNavigate";
import {
  PlayerSettingsState,
  UsePlayerPersistenceReturn,
} from "../../types/PlayerSettingsTypes";

export const usePlayerPersistence = (
  settings: PlayerSettingsState
): UsePlayerPersistenceReturn => {
  const dispatch = useAppDispatch();
  const navigate = useSmartNavigate();

  const saveSettings = useCallback(
    (settingsToSave: PlayerSettingsState, setupMode?: boolean) => {
      const { profile, avatar } = settingsToSave;

      // Create Redux player details structure
      const playerDetailsToSubmit = {
        name: profile.name,
        meta: {
          sprite_id: avatar.spriteId,
          mature: profile.mature,
          accessibility2: profile.accessibility,
          accessibility: false,
          needs_quickstart: profile.needsQuickstart,
        },
      };

      // Update Redux store
      dispatch(updatePlayerDetails(playerDetailsToSubmit));

      // Update localStorage
      localStorage.setItem("playerName", profile.name);
      localStorage.setItem("playerMature", profile.mature.toString());
      localStorage.setItem(
        "playerAccessibility",
        profile.accessibility.toString()
      );
      localStorage.setItem("playerSprite", avatar.spriteId.toString());
      localStorage.setItem("playerNeedsQuickstart", profile.needsQuickstart.toString());

      // Navigate based on mode
      if (setupMode) {
        navigate("/play");
      } else {
        navigate("/");
      }
    },
    [dispatch, navigate]
  );

  const loadFromLocalStorage = useCallback((): Partial<PlayerSettingsState> => {
    const savedName = localStorage.getItem("playerName");
    const savedSprite = localStorage.getItem("playerSprite");
    const savedMature = localStorage.getItem("playerMature");
    const savedAccessibility = localStorage.getItem("playerAccessibility");
    const savedNeedsQuickstart = localStorage.getItem("playerNeedsQuickstart");

    const updates: Partial<PlayerSettingsState> = {};

    if (savedName || savedMature || savedAccessibility || savedNeedsQuickstart) {
      updates.profile = {
        name: savedName || "",
        mature: savedMature === "true",
        accessibility: savedAccessibility ? parseInt(savedAccessibility) : 0,
        needsQuickstart: savedNeedsQuickstart !== null ? savedNeedsQuickstart === "true" : true,
      };
    }

    if (savedSprite) {
      updates.avatar = {
        spriteId: parseInt(savedSprite),
        filteredAvatars: [],
      };
    }

    return updates;
  }, []);

  // Validation based on current settings
  const isValid = settings.profile.name.trim().length >= 3;
  const validationError = !settings.profile.name.trim()
    ? "NO NAME?!"
    : settings.profile.name.trim().length < 3
      ? "Name must be at least 3 characters"
      : undefined;

  return {
    saveSettings,
    loadFromLocalStorage,
    isValid,
    validationError,
  };
};
